import { useState, useEffect, useRef, useCallback } from 'react';
import { getDiagramStatus } from '../IdeationService';

interface UseDiagramStatusPollingProps {
  conversationId: string;
  organizationId: string;
  conversationType: string;
  fieldType: string;
  enabled: boolean;
  onSuccess: (xml: string, diagram_version?: string) => void;
  onError: (error: string) => void;
  onStatusUpdate?: (status: string) => void;
}

export const useDiagramStatusPolling = ({
  conversationId,
  organizationId,
  conversationType,
  fieldType,
  enabled,
  onSuccess,
  onError,
  onStatusUpdate,
}: UseDiagramStatusPollingProps) => {
  const [isPolling, setIsPolling] = useState(false);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const MAX_POLLING_DURATION = 10 * 60 * 1000; // 10 minutes

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const poll = useCallback(async () => {
    if (Date.now() - startTimeRef.current > MAX_POLLING_DURATION) {
      stopPolling();
      onError('Polling timed out after 10 minutes.');
      return;
    }

    try {
      const response = await getDiagramStatus({
        conversation_id: conversationId,
        organization_id: organizationId,
        conversation_type: conversationType,
        field_type: fieldType,
      });

      const statusData = response?.data;
      if (statusData?.is_diagram_status) {
        onStatusUpdate?.(statusData.is_diagram_status);
      }
      // Based on user's payload, status key is 'is_diagram_status' within 'data' object
      if (
        statusData &&
        (statusData.is_diagram_status === 'completed' ||
          statusData.is_diagram_status === 'Completed')
      ) {
        stopPolling();
        onSuccess(statusData.diagramContent || '', statusData?.diagramVersion || '');
      } else if (
        statusData &&
        (statusData.is_diagram_status === 'failed' ||
          statusData.is_diagram_status === 'Failed' ||
          statusData.is_diagram_status === 'error' ||
          statusData.is_diagram_status === 'Error')
      ) {
        stopPolling();
        onError('Diagram generation failed.');
      }
    } catch (error) {
      console.error('Polling error:', error);
      // Continue polling on transient errors
    }
  }, [
    conversationId,
    organizationId,
    conversationType,
    fieldType,
    onSuccess,
    onError,
    onStatusUpdate,
    stopPolling,
  ]);

  useEffect(() => {
    if (enabled) {
      if (!pollingTimerRef.current) {
        setIsPolling(true);
        startTimeRef.current = Date.now();
        // Initial poll immediately
        poll();
        pollingTimerRef.current = setInterval(poll, 5000); // Poll every 5 seconds
      }
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [enabled, poll, stopPolling]);

  return { isPolling };
};
